import { useState } from 'react';
import type { ComparacaoVersoesResponse, ItemComparado, SituacaoItemComparado, VersaoResumoResponse } from '@triade/contracts';
import { ProfitBadge } from '@/components/profit-badge';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { useComparacaoVersoes } from '@/api/orcamentos';
import { formatarMoeda, formatarQuantidade } from '@/lib/format';
import { cn } from '@/lib/utils';
import { deltaLucro, sinalizar } from './formato';

const ROTULO_ITEM: Record<SituacaoItemComparado, string> = {
  INCLUIDO: 'Incluído',
  REMOVIDO: 'Removido',
  ALTERADO: 'Alterado',
  MANTIDO: 'Mantido',
};

const ESTILO_ITEM: Record<SituacaoItemComparado, string> = {
  INCLUIDO: 'bg-blue-100 text-blue-800',
  REMOVIDO: 'bg-danger-bg text-danger-ink',
  ALTERADO: 'bg-warning-bg text-warning-ink',
  MANTIDO: 'bg-line-soft text-ink-body',
};

export interface CompararVersoesProps {
  orcamentoId: string;
  versoes: VersaoResumoResponse[];
  meta: string;
  minimo: string;
}

/** Escolhe duas versoes e mostra GET :id/versoes/comparar. Nenhuma diferenca e calculada aqui: so formatacao. */
export function CompararVersoes({ orcamentoId, versoes, meta, minimo }: CompararVersoesProps) {
  const ordenadas = [...versoes].sort((x, y) => x.numero - y.numero);
  const [a, setA] = useState<number | undefined>(ordenadas[0]?.numero);
  const [b, setB] = useState<number | undefined>(ordenadas[ordenadas.length - 1]?.numero);
  const comparacao = useComparacaoVersoes(orcamentoId, a, b);
  const iguais = a !== undefined && a === b;

  return (
    <section aria-label="Comparar versões" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-caption text-ink-secondary">
          Versão A
          <select aria-label="Versão A" value={a ?? ''} onChange={(e) => setA(Number(e.target.value))} className="h-[34px] rounded-[10px] border border-input bg-card px-3 text-small text-ink">
            {ordenadas.map((v) => (
              <option key={v.id} value={v.numero}>
                {v.numeroExibido} · {v.status === 'RASCUNHO' ? 'rascunho' : v.status.toLowerCase()}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-caption text-ink-secondary">
          Versão B
          <select aria-label="Versão B" value={b ?? ''} onChange={(e) => setB(Number(e.target.value))} className="h-[34px] rounded-[10px] border border-input bg-card px-3 text-small text-ink">
            {ordenadas.map((v) => (
              <option key={v.id} value={v.numero}>
                {v.numeroExibido} · {v.status === 'RASCUNHO' ? 'rascunho' : v.status.toLowerCase()}
              </option>
            ))}
          </select>
        </label>
        {iguais && (
          <p role="status" className="text-small text-ink-muted">
            Escolha duas versões diferentes para comparar.
          </p>
        )}
      </div>

      {!iguais && comparacao.isPending && <p role="status" className="rounded-2xl border border-dashed border-border bg-card px-5 py-8 text-center text-small text-ink-muted">Carregando comparação…</p>}
      {!iguais && comparacao.isError && <ErroFormacao erro={comparacao.error} onTentar={() => void comparacao.refetch()} />}
      {!iguais && comparacao.data && <Resultado c={comparacao.data} meta={meta} minimo={minimo} />}
    </section>
  );
}

function Resultado({ c, meta, minimo }: { c: ComparacaoVersoesResponse; meta: string; minimo: string }) {
  const ladoA = c.a.numeroExibido;
  const ladoB = c.b.numeroExibido;
  return (
    <div data-testid="comparacao" className="flex flex-col gap-4">
      <div className="grid gap-3 md:grid-cols-3">
        <Cartao titulo={ladoA} total={c.totais.a.total} lucro={c.totais.a.lucro} faixa={c.totais.a.faixa} lucroValor={c.totais.a.lucroValor} meta={meta} minimo={minimo} />
        <Cartao titulo={ladoB} total={c.totais.b.total} lucro={c.totais.b.lucro} faixa={c.totais.b.faixa} lucroValor={c.totais.b.lucroValor} meta={meta} minimo={minimo} />
        <div className="flex flex-col gap-1 rounded-2xl border border-border bg-card px-4 py-3 text-small">
          <span className="text-caption text-ink-muted">Diferença (B − A)</span>
          <span data-testid="delta-total" className="tabular text-title font-semibold">{sinalizar(formatarMoeda(c.totais.delta.total), c.totais.delta.total)}</span>
          <span data-testid="delta-lucro" className="tabular text-ink-body">lucro {deltaLucro(c.totais.delta.lucro)}</span>
          {c.totais.delta.lucroValor !== undefined && <span className="tabular text-ink-body">lucro {sinalizar(formatarMoeda(c.totais.delta.lucroValor), c.totais.delta.lucroValor)}</span>}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-small">
        <span data-testid="resumo-incluidos">Incluídos: {c.resumo.incluidos}</span>
        <span data-testid="resumo-removidos">Removidos: {c.resumo.removidos}</span>
        <span data-testid="resumo-alterados">Alterados: {c.resumo.alterados}</span>
        <span data-testid="resumo-mantidos">Mantidos: {c.resumo.mantidos}</span>
      </div>

      {c.itens.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border bg-card px-5 py-8 text-center text-small text-ink-muted">Nenhum item nas duas versões.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] border-collapse text-small" aria-label={`Itens: ${ladoA} e ${ladoB}`}>
            <thead>
              <tr className="text-left text-caption text-ink-muted">
                <th scope="col" className="px-3 py-2.5 font-medium">Item</th>
                <th scope="col" className="px-3 py-2.5 font-medium">Situação</th>
                <th scope="col" className="px-3 py-2.5 text-right font-medium">Qtd. {ladoA} → {ladoB}</th>
                <th scope="col" className="px-3 py-2.5 text-right font-medium">Preço {ladoA} → {ladoB}</th>
                <th scope="col" className="px-3 py-2.5 text-right font-medium">Total {ladoA} → {ladoB}</th>
                <th scope="col" className="px-3 py-2.5 text-right font-medium">Lucro {ladoA} → {ladoB}</th>
                <th scope="col" className="px-3 py-2.5 text-right font-medium">Δ total</th>
                <th scope="col" className="px-3 py-2.5 text-right font-medium">Δ lucro</th>
              </tr>
            </thead>
            <tbody>
              {c.itens.map((i) => (
                <LinhaItem key={i.itemId} item={i} meta={meta} minimo={minimo} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LinhaItem({ item: i, meta, minimo }: { item: ItemComparado; meta: string; minimo: string }) {
  const par = (x: string | null | undefined, y: string | null | undefined, f: (v: string | null | undefined) => string) => (
    <>
      {x === undefined || x === null ? '—' : f(x)} <span aria-hidden="true" className="text-ink-disabled">→</span> {y === undefined || y === null ? '—' : f(y)}
    </>
  );
  return (
    <tr data-testid="item-comparado" data-situacao={i.situacao} className="border-t border-line-soft">
      <td className="px-3 py-3">
        <div className="font-semibold text-ink">{i.codigo}</div>
        <div className="text-caption text-ink-muted">{i.descricao}</div>
      </td>
      <td className="px-3 py-3">
        <span className={cn('rounded-full px-2 py-0.5 text-caption font-semibold', ESTILO_ITEM[i.situacao])}>{ROTULO_ITEM[i.situacao]}</span>
      </td>
      <td className="tabular px-3 py-3 text-right">{par(i.a?.quantidade, i.b?.quantidade, formatarQuantidade)}</td>
      <td className="tabular px-3 py-3 text-right">{par(i.a?.precoUnitario, i.b?.precoUnitario, formatarMoeda)}</td>
      <td className="tabular px-3 py-3 text-right">{par(i.a?.total, i.b?.total, formatarMoeda)}</td>
      <td className="px-3 py-3 text-right">
        <span className="inline-flex items-center gap-1">
          <Lucro lado={i.a} meta={meta} minimo={minimo} />
          <span aria-hidden="true" className="text-ink-disabled">→</span>
          <Lucro lado={i.b} meta={meta} minimo={minimo} />
        </span>
      </td>
      <td className="tabular px-3 py-3 text-right">{i.delta ? sinalizar(formatarMoeda(i.delta.total), i.delta.total) : '—'}</td>
      <td className="tabular px-3 py-3 text-right">{i.delta ? deltaLucro(i.delta.lucro) : '—'}</td>
    </tr>
  );
}

function Lucro({ lado, meta, minimo }: { lado: ItemComparado['a']; meta: string; minimo: string }) {
  if (!lado || lado.lucro === null) return <span className="text-ink-disabled">—</span>;
  return <ProfitBadge lucro={lado.lucro} faixa={lado.faixa ?? undefined} meta={meta} minimo={minimo} compacto />;
}

function Cartao({ titulo, total, lucro, faixa, lucroValor, meta, minimo }: {
  titulo: string; total: string; lucro: string | null; faixa: 'green' | 'orange' | 'red' | null; lucroValor?: string; meta: string; minimo: string;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-2xl border border-border bg-card px-4 py-3 text-small">
      <span className="text-caption text-ink-muted">{titulo}</span>
      <span className="tabular text-title font-semibold">{formatarMoeda(total)}</span>
      <span className="flex items-center gap-2">
        {lucro === null ? <span className="text-ink-disabled">lucro —</span> : <ProfitBadge lucro={lucro} faixa={faixa ?? undefined} meta={meta} minimo={minimo} />}
        {lucroValor !== undefined && <span className="tabular text-ink-body">{formatarMoeda(lucroValor)}</span>}
      </span>
    </div>
  );
}
